/**
 * Role-aware sidebar navigation (TASK-103b).
 *
 * Two static menu trees:
 *  - ADMIN_NAV  — full operations menu per design §3.1.
 *  - SELECTOR_NAV — selector cabinet per design §3.2.
 *
 * Selection rule: `user.role === 'selector'` → SELECTOR_NAV, otherwise ADMIN_NAV.
 * Active state derives from the current `useLocation()` pathname.
 *
 * Badge values come from props (placeholder for now). Real polling lives in
 * TASK-105 / TASK-118 — keeping it as props means we don't bake `react-query`
 * calls into the layout.
 */

import { Link, useLocation } from 'react-router-dom';
import { Box, NavLink, Stack, Text, Tooltip } from '@mantine/core';
import {
  IconMusic,
  IconPlaylist,
  IconTag,
  IconBuildingStore,
  IconUsers,
  IconTicket,
  IconMessage,
  IconChartLine,
  IconHistory,
  IconBell,
  IconSettings,
  IconHome,
  IconUpload,
  IconCash,
  IconInbox,
  IconTransform,
  IconCreditCard,
  IconShieldLock,
  type Icon,
} from '@tabler/icons-react';
import { useAuth } from '@/hooks/use-auth';
import classes from './SidebarNavigation.module.css';

interface NavItem {
  label: string;
  icon: Icon;
  path: string;
  badge?: number;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

export interface SidebarNavigationProps {
  /** Optional badge counts, supplied once polling tasks land. */
  badges?: {
    /** Unified «На модерации» count. Currently tracks-only (the preset list
     *  endpoint is keyset-only with no total — see Sidebar.tsx). */
    moderation?: number;
    unreadTickets?: number;
    unreadNotifications?: number;
  };
  /** Icon-only mode (desktop collapsed sidebar). Tooltips on hover/focus
   *  surface the label. Group headers + badges are hidden. */
  collapsed?: boolean;
}

function buildAdminNav(badges: SidebarNavigationProps['badges']): NavGroup[] {
  // Per CEO 2026-05-24 (round 3): collapse to three sections — Контент,
  // Управление (operational + management entities: accounts, selectors,
  // promos, analytics), Служебное (system inbox / log / settings + the
  // tickets queue). The previous «Клиенты» group merges into Управление
  // since accounts and selectors are both managed entities rather than
  // distinct outward-facing surfaces.
  // TASK-457 (CEO 2026-07-28): «Аналитика» is no longer super-admin-only.
  const management: NavItem[] = [
    { label: 'Аккаунты', icon: IconBuildingStore, path: '/accounts' },
    { label: 'Селекторы', icon: IconUsers, path: '/selectors' },
    { label: 'Промокоды', icon: IconTicket, path: '/promos' },
    { label: 'Аналитика', icon: IconChartLine, path: '/analytics' },
  ];
  return [
    {
      title: 'Контент',
      items: [
        { label: 'Каталог', icon: IconMusic, path: '/catalog' },
        {
          label: 'Подборки',
          icon: IconPlaylist,
          path: '/presets',
        },
        {
          label: 'На модерации',
          icon: IconInbox,
          path: '/moderation',
          badge: badges?.moderation,
        },
        { label: 'Словарь тегов', icon: IconTag, path: '/tag-dictionary' },
      ],
    },
    {
      title: 'Управление',
      items: management,
    },
    {
      title: 'Служебное',
      items: [
        {
          label: 'Обращения',
          icon: IconMessage,
          path: '/tickets',
          badge: badges?.unreadTickets,
        },
        {
          label: 'Заявки на тариф',
          icon: IconCreditCard,
          path: '/tariff-requests',
        },
        { label: 'Журнал', icon: IconHistory, path: '/audit' },
        { label: 'Транскодинг', icon: IconTransform, path: '/transcoding' },
        {
          label: 'Уведомления',
          icon: IconBell,
          path: '/notifications',
          badge: badges?.unreadNotifications,
        },
        { label: 'Настройки', icon: IconSettings, path: '/settings' },
      ],
    },
  ];
}

function buildSelectorNav(badges: SidebarNavigationProps['badges']): NavGroup[] {
  return [
    {
      title: 'Моя работа',
      items: [
        { label: 'Главная', icon: IconHome, path: '/selector/home' },
        { label: 'Загрузка', icon: IconUpload, path: '/selector/upload' },
        { label: 'Мои треки', icon: IconMusic, path: '/selector/tracks' },
        { label: 'Мои подборки', icon: IconPlaylist, path: '/selector/presets' },
        { label: 'Мои промокоды', icon: IconTicket, path: '/selector/promo-codes' },
        { label: 'Мои выплаты', icon: IconCash, path: '/selector/payouts' },
      ],
    },
    {
      title: 'Личное',
      items: [
        {
          label: 'Уведомления',
          icon: IconBell,
          path: '/selector/notifications',
          badge: badges?.unreadNotifications,
        },
        // TASK-450: the selector's only Settings tab — self-service 2FA.
        { label: 'Безопасность', icon: IconShieldLock, path: '/settings/security' },
      ],
    },
  ];
}

function isActivePath(currentPath: string, itemPath: string): boolean {
  if (currentPath === itemPath) return true;
  return currentPath.startsWith(itemPath + '/');
}

export function SidebarNavigation({
  badges,
  collapsed = false,
}: SidebarNavigationProps) {
  const { user } = useAuth();
  const location = useLocation();
  const groups =
    user?.role === 'selector'
      ? buildSelectorNav(badges)
      : buildAdminNav(badges);

  return (
    <Stack gap="xs" component="nav" aria-label="Основная навигация">
      {groups.map((group, idx) => (
        <Box key={group.title} mt={idx === 0 ? 0 : 'md'}>
          {collapsed ? null : (
            <Text
              size="xs"
              tt="uppercase"
              fw={600}
              c="dimmed"
              px="md"
              pb="xs"
              className={classes.groupHeader}
            >
              {group.title}
            </Text>
          )}
          {group.items.map((item) => {
            const Icon = item.icon;
            const active = isActivePath(location.pathname, item.path);
            const navLink = (
              <NavLink
                key={item.path}
                component={Link}
                to={item.path}
                label={collapsed ? undefined : item.label}
                aria-label={collapsed ? item.label : undefined}
                leftSection={<Icon size={18} stroke={1.5} />}
                rightSection={
                  !collapsed && item.badge && item.badge > 0 ? (
                    <Text size="xs" fw={600} c="indigo.7">
                      {item.badge}
                    </Text>
                  ) : null
                }
                active={active}
                variant="filled"
                className={`${classes.navLink} ${collapsed ? classes.navLinkCollapsed : ''}`}
              />
            );
            // Tooltip is what makes icon-only mode usable — show the label on
            // hover or keyboard focus. The collapsed badge count is appended
            // so a non-zero badge is still discoverable.
            if (collapsed) {
              const tooltipLabel =
                item.badge && item.badge > 0
                  ? `${item.label} (${item.badge})`
                  : item.label;
              return (
                <Tooltip
                  key={item.path}
                  label={tooltipLabel}
                  position="right"
                  withArrow
                  openDelay={200}
                >
                  {navLink}
                </Tooltip>
              );
            }
            return navLink;
          })}
        </Box>
      ))}
    </Stack>
  );
}
